import {
  capitulo1,
  capitulo2,
  capitulo3,
  capitulo4,
  capitulo5,
  capitulo6,
  capitulo7,
  capitulo8,
  capituloIntroducao,
} from './capitulos';
import type { Bloco, EntradaSumario } from './blocos';
import type { ManualData } from '../manual-types';

export interface EntradaSumarioNumerada extends EntradaSumario {
  pagina: number;
}

export interface PaginaDocumento {
  id: string;
  numero: number;
  blocos: Bloco[];
}

export interface DocumentoMontado {
  paginas: PaginaDocumento[];
  sumario: EntradaSumarioNumerada[];
  totalPaginas: number;
}

const ENTRADAS_POR_PAGINA_SUMARIO = 30;

/* ------------------------------------------------------------------ */
/* Paginação                                                           */
/* ------------------------------------------------------------------ */

/**
 * Altura útil do miolo em px: 842 (A4) - 72 (cabeçalho) - 96 (rodapé) - 34 (padding).
 * Ver `.manual-doc-body` em `estilos/manual-documento.css`.
 */
export const ALTURA_UTIL = 640;
/** Margem de segurança: a medição arredonda para baixo em textos justificados. */
const FOLGA = 8;
export const LARGURA_UTIL = 471;
const ESPACO_ENTRE_BLOCOS = 9;
const PX_POR_LINHA = 13.6;

/** Altura de um bloco: a medida real da folha quando disponível, senão a estimativa. */
function alturaDoBloco(bloco: Bloco, alturas: Record<string, number> | undefined, escala: number): number {
  const medida = alturas?.[bloco.id];
  if (typeof medida === 'number' && medida > 0) return medida;
  return bloco.peso * PX_POR_LINHA * escala;
}

/** Abaixo disto, encolher o bloco para acompanhar o título estraga a leitura. */
const REDUCAO_MINIMA = 0.85;
/** Quanto uma figura pode encolher para fechar a folha em vez de descer inteira. */
const ENCOLHIMENTO_MAXIMO = 0.8;

function paginar(blocos: Bloco[], alturas: Record<string, number> | undefined, escala: number): Bloco[][] {
  const paginas: Bloco[][] = [];
  const limite = ALTURA_UTIL - FOLGA;
  let atual: Bloco[] = [];
  let ocupado = 0;

  const empilhar = (bloco: Bloco, altura: number) => {
    ocupado += (atual.length ? ESPACO_ENTRE_BLOCOS * escala : 0) + altura;
    atual.push(bloco);
  };

  const alturaDe = (bloco: Bloco) => Math.min(alturaDoBloco(bloco, alturas, escala), limite);

  for (const bruto of blocos) {
    const alturaBruta = alturaDoBloco(bruto, alturas, escala);
    const espaco = atual.length ? ESPACO_ENTRE_BLOCOS * escala : 0;
    const previsto = Math.min(alturaBruta, limite);
    const quebraForcada = Boolean(bruto.novaPagina) && atual.length > 0;
    // Figura que quase cabe fecha a folha reduzida, em vez de deixar um terço
    // da página em branco e descer inteira para a seguinte.
    const cabeEncolhendo = Boolean(bruto.encolhivel)
      && atual.length > 0
      && limite - ocupado - espaco >= alturaBruta * ENCOLHIMENTO_MAXIMO;
    const naoCabe = !cabeEncolhendo && ocupado + espaco + previsto > limite && atual.length > 0;
    // um título de seção no pé da página fica órfão: leva-o para a próxima
    const orfao = bruto.entrada?.nivel === 2
      && atual.length > 0
      && ocupado + espaco + previsto + 60 > limite;

    if (quebraForcada || naoCabe || orfao) {
      // Título não se separa do que ele abre: quando o conteúdo seguinte não
      // cabe, o título desce junto em vez de ficar sozinho no pé da folha.
      let arrastados: Bloco[] = [];
      if (naoCabe && !quebraForcada) {
        while (atual.length > 1 && atual[atual.length - 1].entrada) {
          arrastados.unshift(atual.pop() as Bloco);
        }
        // …mas só se o bloco ainda couber embaixo dele sem encolher demais.
        if (arrastados.length) {
          const alturaTitulos = arrastados.reduce(
            (total, titulo, indice) => total + (indice ? ESPACO_ENTRE_BLOCOS * escala : 0) + alturaDe(titulo),
            0,
          );
          if (limite - alturaTitulos - ESPACO_ENTRE_BLOCOS * escala < alturaBruta * REDUCAO_MINIMA) {
            atual.push(...arrastados);
            arrastados = [];
          }
        }
      }

      paginas.push(atual);
      atual = [];
      ocupado = 0;
      for (const arrastado of arrastados) empilhar(arrastado, alturaDe(arrastado));
    }

    // Nenhum bloco pode transbordar a folha: reduz contra o espaço que REALMENTE
    // sobrou — reduzir contra a folha inteira deixava o excedente sob o rodapé,
    // que tem overflow oculto e cortava a última linha da tabela.
    const folga = atual.length ? ESPACO_ENTRE_BLOCOS * escala : 0;
    const disponivel = Math.max(1, limite - ocupado - folga);
    const reducao = alturaBruta > disponivel ? disponivel / alturaBruta : 1;
    const bloco: Bloco = reducao < 1 ? { ...bruto, escala: reducao } : bruto;
    empilhar(bloco, Math.min(alturaBruta, disponivel));
  }

  if (atual.length) paginas.push(atual);
  return paginas;
}

function blocosSumario(entradas: EntradaSumarioNumerada[], mostrarPaginas: boolean): Bloco[][] {
  const paginas: EntradaSumarioNumerada[][] = [];
  for (let indice = 0; indice < entradas.length; indice += ENTRADAS_POR_PAGINA_SUMARIO) {
    paginas.push(entradas.slice(indice, indice + ENTRADAS_POR_PAGINA_SUMARIO));
  }
  return paginas.map((parte, indice) => ([{
    id: `sumario-${indice}`,
    peso: 0,
    conteudo: (
      <div className="manual-sumario">
        {indice === 0 && <h3 className="manual-titulo-solto">SUMÁRIO</h3>}
        <ul>
          {parte.map((entrada) => (
            <li key={`${entrada.rotulo}-${entrada.pagina}`} className={`nivel-${entrada.nivel}`}>
              <span>{entrada.rotulo}</span>
              <i />
              {mostrarPaginas && <strong>{entrada.pagina}</strong>}
            </li>
          ))}
        </ul>
      </div>
    ),
  }]));
}

/** Blocos do miolo, na ordem editorial — sem paginação. */
export function montarBlocos(dados: ManualData): Bloco[] {
  return [
    ...capituloIntroducao(dados),
    ...capitulo1(dados),
    ...capitulo2(dados),
    ...capitulo3(dados),
    ...capitulo4(dados),
    ...capitulo5(dados),
    ...capitulo6(dados),
    ...capitulo7(dados),
    ...capitulo8(dados),
  ];
}

/** Distribui os blocos em folhas A4, usando as alturas medidas quando houver. */
export function paginarDocumento(
  corpo: Bloco[],
  dados: ManualData,
  alturas?: Record<string, number>,
): DocumentoMontado {
  const escala = (dados.opcoes.densidadeTexto || 100) / 100;
  const paginasCorpo = paginar(corpo, alturas, escala);

  const totalEntradas = corpo.filter((bloco) => bloco.entrada).length;
  const paginasSumario = dados.opcoes.incluirSumario
    ? Math.ceil(totalEntradas / ENTRADAS_POR_PAGINA_SUMARIO)
    : 0;

  const sumario: EntradaSumarioNumerada[] = [];
  paginasCorpo.forEach((blocos, indice) => {
    blocos.forEach((bloco) => {
      if (bloco.entrada) sumario.push({ ...bloco.entrada, pagina: paginasSumario + indice + 1 });
    });
  });

  const paginas: PaginaDocumento[] = [];
  if (paginasSumario > 0) {
    blocosSumario(sumario, dados.opcoes.numerarPaginas).forEach((blocos, indice) => {
      paginas.push({ id: `pagina-sumario-${indice}`, numero: indice + 1, blocos });
    });
  }
  paginasCorpo.forEach((blocos, indice) => {
    paginas.push({ id: `pagina-corpo-${indice}`, numero: paginasSumario + indice + 1, blocos });
  });

  return { paginas, sumario, totalPaginas: paginas.length };
}

export function montarDocumento(dados: ManualData, alturas?: Record<string, number>): DocumentoMontado {
  return paginarDocumento(montarBlocos(dados), dados, alturas);
}
